"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { parseIntegerInput } from "@/lib/parse-number";

type SeparatorMode = "newline" | "space" | "comma" | "none" | "custom";

type Strings = { [K in keyof typeof TEXT_REPEATER_STRINGS]: string };

const SEPARATOR_MODES: { value: SeparatorMode; label: keyof Strings }[] = [
  { value: "newline", label: "separatorNewLine" },
  { value: "space", label: "separatorSpace" },
  { value: "comma", label: "separatorComma" },
  { value: "none", label: "separatorNone" },
  { value: "custom", label: "separatorCustom" },
];

const MIN_REPEAT = 1;
const MAX_REPEAT = 10_000;

function clampCount(raw: string): number {
  const parsed = parseIntegerInput(raw);
  if (parsed === null) {
    return MIN_REPEAT;
  }
  return Math.min(MAX_REPEAT, Math.max(MIN_REPEAT, parsed));
}

function resolveSeparator(mode: SeparatorMode, custom: string): string {
  if (mode === "newline") {
    return "\n";
  }
  if (mode === "space") {
    return " ";
  }
  if (mode === "comma") {
    return ", ";
  }
  if (mode === "custom") {
    return custom;
  }
  return "";
}

function repeatText(
  text: string,
  count: number,
  separator: string,
  numbered: boolean
): string {
  const parts: string[] = [];
  for (let i = 1; i <= count; i++) {
    if (numbered) {
      parts.push(`${i}. ${text}`);
    } else {
      parts.push(text);
    }
  }
  return parts.join(separator);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_REPEATER_STRINGS = {
  textToRepeat: "Text to repeat",
  typeOrPasteTheText: "Type or paste the text you want to repeat",
  repeatCount110000: "Repeat count (1–10,000)",
  separator: "Separator",
  customSeparator: "Custom separator",
  eGOr: "e.g. | or --",
  numberEachRepetition12: "Number each repetition (1., 2., 3., …)",
  repeat: "Repeat",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  repeatedOutput: "Repeated output",
  repetitions: "Repetitions",
  characters: "Characters",
  lines: "Lines",
  separatorNewLine: "New line",
  separatorSpace: "Space",
  separatorComma: "Comma + space",
  separatorNone: "None",
  separatorCustom: "Custom",
} as const;

export default function TextRepeaterWidget() {
  const [input, setInput] = useState("");
  const [countInput, setCountInput] = useState("10");
  const [separatorMode, setSeparatorMode] = useState<SeparatorMode>("newline");
  const [customSeparator, setCustomSeparator] = useState("");
  const [numbered, setNumbered] = useState(false);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const outputCharCount = output.length;
  const outputLineCount = output.length === 0 ? 0 : output.split("\n").length;

  function handleRepeat() {
    if (input.length === 0) {
      return;
    }
    const count = clampCount(countInput);
    setCountInput(String(count));
    const separator = resolveSeparator(separatorMode, customSeparator);
    setOutput(repeatText(input, count, separator, numbered));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  const strings = useToolUi(TEXT_REPEATER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="repeat-input"
        >
          {strings.textToRepeat}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="repeat-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.typeOrPasteTheText}
          value={input}
        />
      </div>
      {/* Options */}
      <section className="space-y-4">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="repeat-count"
          >
            {strings.repeatCount110000}
          </label>
          <input
            className="w-40 rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="repeat-count"
            max={MAX_REPEAT}
            min={MIN_REPEAT}
            onChange={(e) => setCountInput(e.target.value)}
            type="number"
            value={countInput}
          />
        </div>

        <div className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.separator}
          </p>
          <div className="flex flex-wrap gap-3">
            {SEPARATOR_MODES.map((m) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={m.value}
              >
                <input
                  checked={separatorMode === m.value}
                  className="accent-primary"
                  name="separator-mode"
                  onChange={() => setSeparatorMode(m.value)}
                  type="radio"
                  value={m.value}
                />
                {strings[m.label]}
              </label>
            ))}
          </div>
          {separatorMode === "custom" && (
            <div className="space-y-2">
              <label
                className="font-medium text-foreground text-sm"
                htmlFor="custom-separator"
              >
                {strings.customSeparator}
              </label>
              <input
                className="w-60 rounded-lg border border-border bg-card px-4 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                id="custom-separator"
                onChange={(e) => setCustomSeparator(e.target.value)}
                placeholder={strings.eGOr}
                type="text"
                value={customSeparator}
              />
            </div>
          )}
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
          <input
            checked={numbered}
            className="accent-primary"
            onChange={(e) => setNumbered(e.target.checked)}
            type="checkbox"
          />
          {strings.numberEachRepetition12}
        </label>
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleRepeat}
          type="button"
        >
          {strings.repeat}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="repeat-output"
          >
            {strings.repeatedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="repeat-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.repetitions}
            value={fmt(clampCount(countInput))}
          />
          <StatCard label={strings.characters} value={fmt(outputCharCount)} />
          <StatCard label={strings.lines} value={fmt(outputLineCount)} />
        </div>
      )}
    </>
  );
}
